import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Gavel, Wallet, Wrench, FileBarChart2, LogOut, Search, Sparkles, Bell, Gauge, Coins, Boxes, Zap, Recycle, Layers, TrendingUp, PlayCircle, ClipboardCheck, Brain, Sliders, ShieldCheck, Library, MessageSquare, GitCompare, FileCheck2, Award } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { NAV, AUTH, EDI } from "@/constants/testIds";
import AIAssistant from "@/components/AIAssistant";

const NAV_ITEMS = [
    { to: "/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, tid: NAV.dashboard },
    { to: "/projects", label: "Projects", icon: Building2, tid: NAV.projects },
    { to: "/tenders", label: "Tender Management", icon: Gavel, tid: NAV.tenders },
    { to: "/commercial", label: "Commercial", icon: Wallet, tid: NAV.commercial },
    { to: "/engineering", label: "Engineering", icon: Wrench, tid: NAV.engineering },
    { to: "/reporting", label: "Reporting", icon: FileBarChart2, tid: NAV.reporting },
];

const EDI_NAV_ITEMS = [
    { to: "/edi", label: "Enterprise Intelligence™", icon: Boxes, tid: EDI.navHub },
    { to: "/edi/dashboard", label: "EDI Dashboard", icon: Gauge, tid: EDI.navDashboard },
    { to: "/edi/enterprise-value", label: "Enterprise Value", icon: Coins, tid: EDI.navEnterpriseValue },
    { to: "/edi/value-calculator", label: "Value Calculator", icon: Sliders, tid: EDI.navValueCalculator },
    { to: "/edi/engineering-productivity", label: "Engineering Productivity", icon: TrendingUp, tid: EDI.navEngineeringProductivity },
    { to: "/edi/knowledge-intelligence", label: "Knowledge Intelligence", icon: Brain, tid: EDI.navKnowledgeIntelligence },
    { to: "/edi/intellectual-capital", label: "Intellectual Capital", icon: ShieldCheck, tid: EDI.navIntellectualCapital },
    { to: "/edi/reuse-savings", label: "Reuse Savings", icon: Layers, tid: EDI.navReuseSavings },
    { to: "/edi/knowledge-reuse", label: "Knowledge Reuse", icon: Recycle, tid: EDI.navKnowledgeReuse },
    { to: "/edi/capture-once", label: "Capture Once. Use Many Times.", icon: Zap, tid: EDI.navCaptureOnce },
    { to: "/edi/board-brief", label: "Board Brief", icon: ClipboardCheck, tid: EDI.navBoardBrief },
    { to: "/edi/board-room", label: "Board Room", icon: ClipboardCheck, tid: EDI.navBoardRoom },
    { to: "/edi/ai-advisor", label: "AI Executive Advisor", icon: MessageSquare, tid: EDI.navAiAdvisor },
    { to: "/edi/reuse-intelligence", label: "Reuse Intelligence", icon: GitCompare, tid: EDI.navReuseIntelligence },
    { to: "/edi/demo-centre", label: "Demonstration Centre", icon: Library, tid: EDI.navDemoCentre },
    { to: "/edi/executive-demo", label: "Executive Rehearsal", icon: PlayCircle, tid: EDI.navExecutiveDemo },
    { to: "/edi/acceptance-audit", label: "Acceptance Audit", icon: FileCheck2, tid: EDI.navAcceptanceAudit },
    { to: "/edi/certification", label: "Production Certification", icon: Award, tid: EDI.navCertification },
    { to: "/edi/capability-maturity", label: "Capability Maturity", icon: Layers, tid: EDI.navCapabilityMaturity },
    { to: "/edi/client-portal", label: "Client Portal", icon: Building2, tid: EDI.navClientPortal },
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();

    const onLogout = async () => {
        await logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen w-full flex bg-[var(--sgsb-bg)] text-slate-100">
            {/* Sidebar */}
            <aside
                data-testid={NAV.sidebar}
                className="hidden md:flex fixed h-screen w-[260px] flex-col border-r border-slate-800/70 bg-[var(--sgsb-surface)] z-20"
            >
                <div className="px-6 pt-6 pb-5 border-b border-slate-800/70">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-md border border-[color:var(--sgsb-gold)]/60 grid place-items-center">
                            <span className="font-display font-black text-[var(--sgsb-gold)] text-sm">SGS</span>
                        </div>
                        <div>
                            <div className="font-display font-black tracking-tight text-white text-[15px] leading-none">
                                SGS BATEMAN
                            </div>
                            <div className="kicker text-[var(--sgsb-gold)] text-[10px] mt-1">EPCM360™</div>
                        </div>
                    </div>
                </div>

                <div className="px-3 py-5 flex-1 overflow-y-auto">
                    <div className="kicker text-slate-500 px-3 mb-3">Workspace</div>
                    <nav className="space-y-1">
                        {NAV_ITEMS.map(({ to, label, icon: Icon, tid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                data-testid={tid}
                                className={({ isActive }) =>
                                    `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-body transition-colors duration-200 ${
                                        isActive
                                            ? "bg-slate-800/60 text-[var(--sgsb-gold)]"
                                            : "text-slate-300 hover:bg-slate-800/40 hover:text-white"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && (
                                            <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[var(--sgsb-gold)] rounded-r-full" />
                                        )}
                                        <Icon
                                            className={`h-[18px] w-[18px] ${isActive ? "text-[var(--sgsb-gold)]" : "text-slate-400 group-hover:text-slate-200"}`}
                                            strokeWidth={1.5}
                                        />
                                        <span className="tracking-tight">{label}</span>
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="kicker text-slate-500 px-3 mt-8 mb-3">Intelligence</div>
                    <nav className="space-y-1">
                        {EDI_NAV_ITEMS.map(({ to, label, icon: Icon, tid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={to === "/edi"}
                                data-testid={tid}
                                className={({ isActive }) =>
                                    `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-body transition-colors duration-200 ${
                                        isActive
                                            ? "bg-slate-800/60 text-[var(--sgsb-gold)]"
                                            : "text-slate-300 hover:bg-slate-800/40 hover:text-white"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {isActive && (
                                            <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[var(--sgsb-gold)] rounded-r-full" />
                                        )}
                                        <Icon
                                            className={`h-[18px] w-[18px] ${isActive ? "text-[var(--sgsb-gold)]" : "text-slate-400 group-hover:text-slate-200"}`}
                                            strokeWidth={1.5}
                                        />
                                        <span className="tracking-tight">{label}</span>
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="kicker text-slate-500 px-3 mt-8 mb-3">Assistant</div>
                    <div className="mx-3 rounded-md border border-[color:var(--sgsb-gold)]/25 bg-gradient-to-b from-slate-900/70 to-slate-950/70 p-4">
                        <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                            <span className="kicker text-[10px]">Executive AI</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-400 font-body leading-relaxed">
                            Ask GPT-5.2 about portfolio risks, tenders, cost variances and generate executive reports.
                        </p>
                    </div>
                </div>

                <div className="p-4 border-t border-slate-800/70">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 border border-slate-700 grid place-items-center font-display font-bold text-slate-200">
                            {(user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-100 truncate font-body">{user?.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{user?.title || user?.role}</div>
                        </div>
                        <button
                            data-testid={AUTH.logout}
                            onClick={onLogout}
                            title="Log out"
                            className="p-2 rounded-md text-slate-400 hover:text-[var(--sgsb-gold)] hover:bg-slate-800/60 transition-colors duration-200"
                        >
                            <LogOut className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 md:pl-[260px]">
                <header className="sticky top-0 z-10 border-b border-slate-800/70 bg-[var(--sgsb-bg)]/85 backdrop-blur">
                    <div className="flex items-center gap-4 px-6 lg:px-10 h-[72px]">
                        <div className="hidden md:flex items-center gap-2 text-slate-500 text-xs font-body">
                            <span className="kicker text-[10px] text-[var(--sgsb-gold)]">SGS Bateman</span>
                            <span className="text-slate-600">/</span>
                            <span className="tracking-wide uppercase">Executive Workspace</span>
                        </div>

                        <div className="flex-1" />

                        <div className="hidden lg:flex items-center gap-2 w-[320px] rounded-md bg-slate-900/60 border border-slate-800 px-3 py-2 text-slate-400">
                            <Search className="h-4 w-4" strokeWidth={1.5} />
                            <input
                                placeholder="Search projects, tenders, contracts…"
                                className="bg-transparent outline-none flex-1 text-sm font-body placeholder:text-slate-600"
                            />
                            <span className="kicker text-[9px] text-slate-500">⌘K</span>
                        </div>

                        <button className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors duration-200" title="Notifications">
                            <Bell className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </header>

                <main className="px-6 lg:px-10 py-8">
                    <Outlet />
                </main>
            </div>

            <AIAssistant />
        </div>
    );
}
